import { Permission } from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, RefreshCw, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	fetchOrganizationByIdQueryOptions,
	useResetOrganizationTrial,
} from "@/api/organizations";
import { Button } from "@/components/ui/button";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute(
	"/_auth/app/_app/organizations/$organizationId/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { organizationId } = params;

		// Preload organization data
		context.queryClient.ensureQueryData(
			fetchOrganizationByIdQueryOptions(token, organizationId),
		);

		return { userId, token, organizationId };
	},
	component: OrganizationOverview,
});

function OrganizationOverview() {
	const { t } = useTranslation();
	const { token, organizationId } = Route.useLoaderData();
	const can = useCan();

	const { data: organization } = useSuspenseQuery(
		fetchOrganizationByIdQueryOptions(token, organizationId),
	);

	const resetTrialMutation = useResetOrganizationTrial();
	const canManageOrganizations = can(Permission.MANAGE_ORGANIZATIONS);

	const handleResetTrial = () => {
		resetTrialMutation.mutate({ token, organizationId });
	};

	return (
		<div className="space-y-6" data-testid="organization-overview">
			{/* Quick Actions - Show reset trial button when trial is not available and user can manage */}
			{canManageOrganizations && !organization.isTrialAvailable && (
				<div className="flex justify-end">
					<Button
						variant="outline"
						size="sm"
						onClick={handleResetTrial}
						disabled={resetTrialMutation.isPending}
					>
						<RefreshCw
							className={`h-4 w-4 mr-2 ${resetTrialMutation.isPending ? "animate-spin" : ""}`}
						/>
						{t("organizations.actions.resetTrial")}
					</Button>
				</div>
			)}

			{/* Organization Details Card */}
			<div className="card bg-base-100 shadow-sm">
				<div className="card-body">
					<div className="flex items-center gap-3 mb-4">
						<Building2 className="h-6 w-6 text-primary" />
						<h2 className="card-title text-lg">
							{t("organizations.details.overview.title")}
						</h2>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.name")}
							</span>
							<p className="text-base-content font-medium">
								{organization.name}
							</p>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.description")}
							</span>
							<p className="text-base-content">
								{organization.description ||
									t("organizations.details.info.noDescription")}
							</p>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.language")}
							</span>
							<p className="text-base-content">
								{organization.defaultLanguage}
							</p>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.currency")}
							</span>
							<p className="text-base-content">
								{organization.defaultCurrency}
							</p>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.trialAvailable")}
							</span>
							<span
								className={`badge badge-sm ${
									organization.isTrialAvailable
										? "badge-success"
										: "badge-neutral"
								}`}
							>
								{organization.isTrialAvailable
									? t("organizations.trial.yes")
									: t("organizations.trial.no")}
							</span>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.platformCustomerId")}
							</span>
							<p className="text-base-content font-mono text-sm">
								{organization.platformCustomerId || "-"}
							</p>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.createdAt")}
							</span>
							<p className="text-base-content">
								{new Date(organization.createdAt).toLocaleDateString()}
							</p>
						</div>

						<div>
							<span className="text-sm font-medium text-base-content/70 block">
								{t("organizations.details.info.updatedAt")}
							</span>
							<p className="text-base-content">
								{new Date(organization.updatedAt).toLocaleDateString()}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Owner Card */}
			<div className="card bg-base-100 shadow-sm">
				<div className="card-body">
					<div className="flex items-center gap-3 mb-4">
						<User className="h-6 w-6 text-primary" />
						<h2 className="card-title text-lg">
							{t("organizations.details.owner.title")}
						</h2>
					</div>

					{organization.owner ? (
						<div className="flex items-center gap-4">
							<div className="avatar">
								<div className="w-12 h-12 rounded-full bg-base-300 flex items-center justify-center">
									{organization.owner.imageUrl ? (
										<img
											src={organization.owner.imageUrl}
											alt={organization.owner.email}
											className="rounded-full"
										/>
									) : (
										<User className="w-6 h-6 text-base-content/60" />
									)}
								</div>
							</div>
							<div>
								<p className="font-medium text-base-content">
									{organization.owner.firstName && organization.owner.lastName
										? `${organization.owner.firstName} ${organization.owner.lastName}`
										: organization.owner.email}
								</p>
								<p className="text-sm text-base-content/70">
									{organization.owner.email}
								</p>
							</div>
						</div>
					) : (
						<p className="text-base-content/70">
							{t("organizations.details.owner.noOwner")}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
